import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Politique de confidentialité — content lives in i18n under
 * `legal.privacy.*` and is fully translated to fr/en/de.
 */
export function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t('legal.privacy.documentTitle');
  }, [t, i18n.language]);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
      >
        {t('legal.privacy.overline')}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 4 }}>
        {t('legal.privacy.title')}
      </Typography>

      <Box sx={{ '& p, & li': { lineHeight: 1.8, color: 'text.primary' } }}>
        <Section title={t('legal.privacy.intro.title')}>
          <Typography>{t('legal.privacy.intro.p1')}</Typography>
          <Typography>{t('legal.privacy.intro.p2')}</Typography>
          <Typography>{t('legal.privacy.intro.p3')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s1.title')}>
          <Typography>{t('legal.privacy.s1.p1')}</Typography>
          <BulletsKey k="legal.privacy.s1.bullets1" />
          <Typography>{t('legal.privacy.s1.p2')}</Typography>
          <BulletsKey k="legal.privacy.s1.bullets2" />
        </Section>

        <Section title={t('legal.privacy.s2.title')}>
          <Typography>{t('legal.privacy.s2.p1')}</Typography>
          <BulletsKey k="legal.privacy.s2.bullets" />
          <Typography>{t('legal.privacy.s2.p2')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s3.title')}>
          <Typography>{t('legal.privacy.s3.p1')}</Typography>
          <BulletsKey k="legal.privacy.s3.bullets" />
        </Section>

        <Section title={t('legal.privacy.s4.title')}>
          <Typography>{t('legal.privacy.s4.p1')}</Typography>
          <Typography>{t('legal.privacy.s4.p2')}</Typography>
          <Typography>{t('legal.privacy.s4.p3')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s5.title')}>
          <Typography>{t('legal.privacy.s5.p1')}</Typography>
          <BulletsKey k="legal.privacy.s5.bullets" />
        </Section>

        <Section title={t('legal.privacy.s6.title')}>
          <Typography>{t('legal.privacy.s6.p1')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s7.title')}>
          <Typography>{t('legal.privacy.s7.p1')}</Typography>
          <BulletsKey k="legal.privacy.s7.bullets" />
          <Typography>
            {t('legal.privacy.s7.p2Before')}
            <Mail to="info@winorgroup.com" />
          </Typography>
        </Section>

        <Section title={t('legal.privacy.s8.title')}>
          <Typography>{t('legal.privacy.s8.p1')}</Typography>
          <Typography>{t('legal.privacy.s8.p2')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s9.title')}>
          <Typography>{t('legal.privacy.s9.p1')}</Typography>
          <Typography>{t('legal.privacy.s9.p2')}</Typography>
        </Section>

        <Section title={t('legal.privacy.s10.title')}>
          <Typography>{t('legal.privacy.s10.p1')}</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1, listStyle: 'none' }}>
            <li>📧 <Mail to="info@winorgroup.com" /></li>
            <li>📍 {t('legal.contact.city')}</li>
            <li>🏢 {t('legal.contact.publisher')}</li>
          </Box>
        </Section>
      </Box>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </>
  );
}

function BulletsKey({ k }: { k: string }) {
  const { t } = useTranslation();
  const items = t(k, { returnObjects: true }) as string[];
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Box>
  );
}

function Mail({ to }: { to: string }) {
  return (
    <Link href={`mailto:${to}`} sx={{ color: 'primary.main', fontWeight: 500 }}>
      {to}
    </Link>
  );
}
